import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatMenu } from '@angular/material/menu';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';
import * as fromAuth from '../../../../store/auth';
import * as fromUi from '../../../../store/ui';
import * as fromEvents from '../../../customer-shared/tracking/events';
import { TrackingService } from '../../services/tracking.service';
import { PwaService } from '../../../customer-shared/services/install-pwa';
import { SignalCenterService } from '../../../signal-center/services/signal-center.service';
import { UsersManagementService } from '../../../user/services/users-management.service';

// The settings menu shared by the customer header and the mobile page header. The headers keep their
// own button (styled for their bar) and open this with [matMenuTriggerFor]="settingsMenu.menu".
@Component({
  selector: 'app-settings-menu',
  templateUrl: './settings-menu.component.html',
  styleUrls: ['./settings-menu.component.scss'],
  standalone: false,
})
export class SettingsMenuComponent {
  @ViewChild(MatMenu, { static: true }) menu: MatMenu;

  locations$ = this.store.select(fromAuth.selectAvailableLocations);
  selectedLocationId$ = this.store.select(fromUi.selectSelectedLocationId);
  isExecutive$ = this.store.select(fromAuth.selectIsExecutive);
  showInstall$ = this.pwa.installPrompt$;

  alertBadge$ = this.selectedLocationId$.pipe(
    distinctUntilChanged(),
    filter((locationId): locationId is number => locationId != null),
    switchMap(locationId => this.signalCenterService.getAlertCount(locationId).pipe(catchError(() => of(0)))),
    map(count => (count > 0 ? String(count) : null)),
    // Read by the header's gear badge and the Devices item - one request for both.
    shareReplay(1),
  );

  missingAlertRecipient$ = combineLatest([this.selectedLocationId$.pipe(distinctUntilChanged()), this.isExecutive$]).pipe(
    switchMap(([locationId, isExecutive]) => {
      if (!isExecutive || locationId == null) return of(false);
      return this.usersManagementService.locationsWithoutAlertRecipient$.pipe(
        map(locations => locations.some(l => l.locationId === locationId)),
      );
    }),
    shareReplay(1),
  );

  constructor(
    private store: Store<{ ui: fromUi.UiState; auth: fromAuth.AuthState }>,
    private router: Router,
    private tracking: TrackingService,
    private pwa: PwaService,
    private signalCenterService: SignalCenterService,
    private usersManagementService: UsersManagementService,
  ) {}

  opened() {
    this.tracking.track(fromEvents.settingsClicked());
  }

  goToDevices() {
    this.selectedLocationId$.pipe(take(1), withLatestFrom(this.locations$)).subscribe(([locationId, locations]) => {
      const targetLocationId = locationId ?? locations[0]?.id;
      if (targetLocationId != null) this.router.navigate(['locations', targetLocationId, 'signal-center']);
    });
  }

  goToUsers() {
    this.router.navigate(['/user/users']);
  }

  installPwa() {
    this.pwa.promptInstall().then(res => console.log(res));
  }
}
